import { useState, useEffect, useRef } from "react";
import "./EditCart.css";
import { X } from "lucide-react";
import ApiService from "../../components/Api_services";
import AddToCartSuccess from "../addtocart_v2/AddToCartSuccess";
import MultiPurpose from "../multipurpose_v2/MultiPurpose";

const EditCart = ({
  onClose,
  id,
  itemId,
  customerId,
  quotationId,
  itemQty,
  pricingName,
  package_seperate,
  packing_quantity,
  cs_id,
  bundleId, // optional: bundle id for support/package items
  branchRegion,
}) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchQuantities, setBranchQuantities] = useState({});
  const [invalidBranchQty, setInvalidBranchQty] = useState({});
  const [showAddToCartSuccess, setShowAddToCartSuccess] = useState({
    show: false,
    tick: false,
    text: "",
  });
  const [multiPurpose, setMultiPurpose] = useState({
    show: false,
    message: "",
  });
  const stepValue =
    package_seperate === "1" && Number(packing_quantity) > 0
      ? Number(packing_quantity)
      : 1;
  let normalQty = itemQty;
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoading(true);
        const params = branchRegion ? { region: branchRegion } : {};
        const rawBranches = await ApiService.getBranchList(params);
        // Ensure HQ (branch_id === "0") is always first; if absent (region-only list), first element becomes default
        const branchData = [
          ...rawBranches.filter((b) => String(b.branch_id) === "0"),
          ...rawBranches.filter((b) => String(b.branch_id) !== "0"),
        ];
        setBranches(branchData);
      } catch (error) {
        alert("Failed to load branches");
      } finally {
        setLoading(false);
      }
    };
    fetchBranches();
  }, [branchRegion]);

  const calculateHQQuantity = () => {
    if (branches.length === 0) return normalQty;
    const hqBranchId = branches[0].branch_id;
    let distributedQty = 0;
    Object.entries(branchQuantities).forEach(([branchId, qty]) => {
      if (branchId !== hqBranchId) distributedQty += qty;
    });
    return normalQty - distributedQty;
  };

  const handleDecrease = (branchId) => {
    if (branches.length === 0) return;

    const hqBranchId = branches[0].branch_id;

    // Cannot modify HQ
    if (branchId === hqBranchId) return;

    setBranchQuantities((prev) => {
      const newQuantities = { ...prev };
      const currentQty = newQuantities[branchId] || 0;

      if (currentQty >= stepValue) {
        // Decrease from this branch
        newQuantities[branchId] = currentQty - stepValue;
        // Update HQ to reflect the change
        newQuantities[hqBranchId] = calculateHQQuantity() + stepValue;
      }

      return newQuantities;
    });
  };

  const handleIncrease = (branchId) => {
    if (branches.length === 0) return;
    const hqBranchId = branches[0].branch_id;
    if (branchId === hqBranchId) return;

    setBranchQuantities((prev) => {
      const newQuantities = { ...prev };
      const currentHQQty = calculateHQQuantity();

      // 计算所有分店已分配总数（不含HQ）
      const totalAssigned = Object.entries(newQuantities)
        .filter(([bid]) => bid !== hqBranchId)
        .reduce((sum, [, qty]) => sum + qty, 0);

      if (totalAssigned + stepValue <= normalQty) {
        newQuantities[branchId] = (newQuantities[branchId] || 0) + stepValue;
        // HQ 自动分配剩余
        newQuantities[hqBranchId] =
          normalQty -
          Object.entries(newQuantities)
            .filter(([bid]) => bid !== hqBranchId)
            .reduce((sum, [, qty]) => sum + qty, 0);
      }

      return newQuantities;
    });
  };

  const handleQuantityInputChange = (branchId, value) => {
    if (branches.length === 0) return;
    const hqBranchId = branches[0].branch_id;
    if (branchId === hqBranchId) return;
    if (!/^\d*$/.test(value)) return;
    let numValue = parseInt(value) || 0;

    setBranchQuantities((prev) => {
      const newQuantities = { ...prev };
      let otherBranchesTotal = 0;
      Object.entries(prev).forEach(([bId, qty]) => {
        if (bId !== branchId && bId !== hqBranchId) {
          otherBranchesTotal += qty;
        }
      });
      const maxValue = normalQty - otherBranchesTotal;
      if (numValue > maxValue) {
        newQuantities[branchId] = maxValue;
      } else {
        newQuantities[branchId] = numValue;
      }
      const newDistributedTotal = otherBranchesTotal + newQuantities[branchId];
      newQuantities[hqBranchId] = normalQty - newDistributedTotal;

      // After updating, check if the actual value is valid and update invalid state accordingly
      let actualValue = newQuantities[branchId];
      let actualInvalid = false;
      if (package_seperate === "1" && Number(packing_quantity) > 0) {
        if (actualValue % Number(packing_quantity) !== 0) {
          actualInvalid = true;
        }
      }
      setInvalidBranchQty((prev) => ({ ...prev, [branchId]: actualInvalid }));

      return newQuantities;
    });
  };

  const handleEditCart = async () => {
    try {
      // Build distributions from branches state
      const distributions = branches
        .map((branch, index) => ({
          branch_id: branch.branch_id,
          quantity:
            index === 0
              ? calculateHQQuantity()
              : branchQuantities[branch.branch_id] || 0,
        }))
        .filter((dist) => dist.quantity > 0); // exclude branches with 0 quantity

      const formData = new URLSearchParams();
      formData.append("id", id);
      if (bundleId) formData.append("bundle_id", bundleId);
      if (cs_id) formData.append("cs_id", cs_id);
      formData.append("item_id", itemId);
      formData.append("customer_id", customerId);
      formData.append("quotation_id", quotationId ?? "");
      formData.append("distributions", JSON.stringify(distributions));

      const result = await ApiService.editCartItemBranch(formData);

      if (result.status === 200) {
        setShowAddToCartSuccess({
          show: true,
          tick: true,
          text: "Cart updated successfully!",
        });
      } else {
        setMultiPurpose({
          show: true,
          message: result.message || "Failed to update cart. Please try again.",
        });
      }
    } catch (error) {
      console.error("Unexpected error editing cart:", error);
      setMultiPurpose({
        show: true,
        message: "An unexpected error occurred. Please try again.",
      });
    }
  };

  return (
    <div className="addtocart_v2">
      <div className="addtocart-content_v2" ref={modalRef}>
        {showAddToCartSuccess.show && (
          <AddToCartSuccess
            text={showAddToCartSuccess.text}
            tick={showAddToCartSuccess.tick}
            onClose={() => {
              setShowAddToCartSuccess({ show: false, text: "" });
              onClose();
              window.location.reload();
            }}
          />
        )}

        {multiPurpose.show && (
          <MultiPurpose
            title="Error"
            message={multiPurpose.message}
            yesText="OK"
            hideNo={true}
            onYes={() => setMultiPurpose({ show: false, message: "" })}
            onClose={() => setMultiPurpose({ show: false, message: "" })}
          />
        )}

        <div className="addtocart-title-container_v2">
          <div className="addtocart-title_v2">
            <strong>Edit Branches</strong>
          </div>
          <button className="atcclose-button_v2" onClick={() => onClose()}>
            <X />
          </button>
        </div>

        <div className="addtocart-main_v2">
          <div className="addtocart-producttype_v2">
            Product Type : {pricingName}
          </div>

          <div className="atc-totalqty-container_v2">
            <div className="atc-totalqty_v2">Total Quantity :</div>
            <div className="atc-totalqty-value_v2">{itemQty}</div>
          </div>

          <div className="atc-branch-header_v2">
            <div className="subtitle-col1_v2" style={{ fontSize: "16px" }}>
              Stock Allocation
            </div>
            <div className="subtitle-remaincol_v2">Quantity</div>
          </div>

          <div className="atc-branches_v2">
            {branches.map((branch, index) => {
              const isHQ = index === 0;
              const displayQuantity = isHQ
                ? calculateHQQuantity()
                : branchQuantities[branch.branch_id] || 0;

              return (
                <div className="atc-branch_v2" key={branch.branch_id}>
                  <div className="atc-branchname-container_v2">
                    <div className="atc-branchname_v2">
                      {branch.branch_code}
                      {isHQ && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#717182",
                            fontStyle: "italic",
                          }}
                        >
                          (Default - Auto calculated)
                        </span>
                      )}
                    </div>

                    {/* Quantity */}
                    <div className="atc-productqty-container_v2">
                      <div className="atc-productqty_v2">
                        <button
                          className="minus-btn_v2"
                          onClick={() => handleDecrease(branch.branch_id)}
                          type="button"
                          disabled={isHQ}
                        >
                          −
                        </button>
                        <input
                          type="text"
                          className="atc-productqty-value_v2"
                          value={displayQuantity}
                          onChange={(e) =>
                            handleQuantityInputChange(
                              branch.branch_id,
                              e.target.value,
                            )
                          }
                          readOnly={isHQ}
                        />
                        <button
                          className="plus-btn_v2"
                          onClick={() => handleIncrease(branch.branch_id)}
                          type="button"
                          disabled={isHQ}
                        >
                          +
                        </button>
                      </div>
                      {invalidBranchQty[branch.branch_id] && (
                        <div className="quantity-error">
                          Please enter a quantity that is a multiple of{" "}
                          {packing_quantity}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="atc-addtocart-container_v2">
            <button
              className="atc-addtocart-btn_v2 white"
              type="button"
              onClick={() => onClose()}
            >
              Cancel
            </button>
            <button
              className="atc-addtocart-btn_v2"
              type="button"
              onClick={() => handleEditCart()}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCart;
